/**
 * Two-factor login challenge (PRE_LAUNCH H11 / F7).
 *
 * Rendered after a successful email + password login when the server
 * returns { requires2FA: true, challengeToken }. Accepts either a 6-digit
 * TOTP code from the authenticator app OR one of the 8 one-time recovery
 * codes (format ABCD-1234-EF).
 *
 * On success, the parent stores the user + tokens via setCredentials and
 * navigates to /dashboard.
 */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Alert, Link, CircularProgress,
} from '@mui/material';
import api from '../../services/api';
import { setCredentials } from '../../store/authSlice';

interface Props {
  challengeToken: string;
  email: string;
  onCancel: () => void;
}

const TwoFactorChallenge: React.FC<Props> = ({ challengeToken, email, onCancel }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = code.trim();
    if (useRecovery && trimmed.length < 8) { setError('Recovery code looks too short'); return; }
    if (!useRecovery && !/^\d{6}$/.test(trimmed)) { setError('Enter the 6-digit code from your app'); return; }
    setBusy(true); setError(null);
    try {
      const resp = await api.post('/auth/login/2fa', { challengeToken, code: trimmed });
      const data = resp.data as any;
      dispatch(setCredentials({ user: data.user, token: data.token, refreshToken: data.refreshToken }));
      // Warn when recovery codes are running low.
      if (typeof data.recoveryCodesRemaining === 'number' && data.recoveryCodesRemaining <= 2) {
        // Toast via parent would be cleaner; for now use console as a non-blocking signal.
        // eslint-disable-next-line no-console
        console.warn(`[2FA] Only ${data.recoveryCodesRemaining} recovery codes left — regenerate from Settings.`);
      }
      navigate('/dashboard');
    } catch (err: any) {
      const errCode = err?.response?.data?.error?.code;
      if (errCode === 'CHALLENGE_EXPIRED') {
        setError('The 2FA challenge expired. Sign in again.');
      } else if (errCode === 'TOTP_INVALID') {
        setError('Code did not match. Check your authenticator clock and try again.');
      } else {
        setError(err?.response?.data?.error?.message || 'Verification failed');
      }
    } finally { setBusy(false); }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 360 }}>
      <Typography variant="h5" align="center" sx={{ fontWeight: 700, mb: 1 }}>
        Two-Factor Verification
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
        Signing in as <strong>{email}</strong>
      </Typography>

      {useRecovery ? (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Enter one of your saved recovery codes (format <code>ABCD-1234-EF</code>):
          </Typography>
          <TextField
            fullWidth autoFocus size="small" label="Recovery code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            inputProps={{ maxLength: 12 }}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            sx={{ mb: 2 }}
          />
        </>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Enter the 6-digit code from your authenticator app:
          </Typography>
          <TextField
            fullWidth autoFocus size="small" label="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Button fullWidth variant="contained" onClick={submit} disabled={busy} sx={{ mb: 1.5 }}>
        {busy ? <CircularProgress size={22} /> : 'Verify & Sign In'}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Link component="button" onClick={() => { setUseRecovery(s => !s); setCode(''); setError(null); }} sx={{ fontSize: '0.85rem' }}>
          {useRecovery ? 'Use authenticator code instead' : 'Use a recovery code instead'}
        </Link>
        <Link component="button" onClick={onCancel} sx={{ fontSize: '0.85rem' }}>
          Back to sign-in
        </Link>
      </Box>
    </Paper>
  );
};

export default TwoFactorChallenge;
